import type { Container, CurrentUser, LoginPanel } from "@/types/general";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { apiClient } from "@/lib/apiClient";
import { getUserTypeFromAuthResponse } from "@/core/auth/utils/token.utils";
import { isAdminLoginAllowed } from "@/constants/auth";

/**
 * Oturumdaki kullanıcının izin anahtarları. Kursiyerler de çağırdığı için
 * /api/admin altında değil (pentest 3.2.2: /api/admin/* personel dışına 403).
 */
const MY_PERMISSIONS_ENDPOINT = "/api/users/me/permissions";
/**
 * Geçiş dönemi yedeği: yeni uç henüz deploy edilmemiş backend'de 404 döner.
 * Backend yayınlandıktan sonra kaldırılabilir.
 */
const LEGACY_MY_PERMISSIONS_ENDPOINT =
  "/api/admin/permissions/users/me/permissions";
const USER_ME_ENDPOINT = "/api/users/me";

type MyPermissionsResponse = { data?: { permissionKeys?: string } };

const isNotFoundError = (error: unknown): boolean =>
  (error as { response?: { status?: number } } | null)?.response?.status ===
  404;

/** GET /api/users/me/permissions; uç yoksa (404) eski admin yoluna düşer. */
const fetchMyPermissions = async (): Promise<MyPermissionsResponse> => {
  try {
    // 404 → yedek uca düşülür; o durumda toast gösterilmesin.
    const { data } = await apiClient.get<MyPermissionsResponse>(
      MY_PERMISSIONS_ENDPOINT,
      { skipErrorToast: true },
    );
    return data;
  } catch (error) {
    if (!isNotFoundError(error)) throw error;
    const { data } = await apiClient.get<MyPermissionsResponse>(
      LEGACY_MY_PERMISSIONS_ENDPOINT,
    );
    return data;
  }
};

export const useStore = create<Container>()(
  persist(
    (set, get) => ({
      clearCurrentUser: () => {
        set({
          currentUser: null,
          isInitialized: false,
          isStaff: false,
          userType: null,
          logo: null,
          permissionsArray: [],
        });
      },

      currentUser: null,

      fetchCurrentUser: async (forceRefresh = false) => {
        try {
          const state = get();

          if (!forceRefresh && state.permissionsArray.length > 0) {
            return;
          }

          const userResponse = null;

          let permissionsArray: string[] = [];
          try {
            const currentUserId = state.currentUser?.id;

            if (currentUserId) {
              const data = await fetchMyPermissions();

              const permissionKeys = data?.data?.permissionKeys;

              if (typeof permissionKeys === "string" && permissionKeys.trim()) {
                permissionsArray = permissionKeys
                  .split(/,\s*/)
                  .map((p) => p.trim())
                  .filter((p) => p.length > 0);
              }
            }
          } catch (permissionsError) {
            console.error("❌ Permissions fetch failed:", permissionsError);
          }

          set({
            currentUser: userResponse || state.currentUser,
            permissionsArray,
            isInitialized: true,
          });
        } catch (error) {
          console.error("❌ Error in fetchCurrentUser:", error);
          set({ isInitialized: true, logo: null, permissionsArray: [] });
        }
      },

      refreshCurrentUser: async () => {
        try {
          const { data } = await apiClient.get<{ data?: CurrentUser }>(
            USER_ME_ENDPOINT,
          );
          const user = data?.data;
          if (user) {
            set((state) => ({
              currentUser: { ...state.currentUser, ...user } as CurrentUser,
            }));
          }
        } catch (error) {
          console.error("❌ Error refreshing current user:", error);
        }
      },

      isInitialized: false,
      isStaff: false,
      userType: null,
      logo: null,

      permissionsArray: [],

      /**
       * Girişten dönen yanıtı store'a yazar ve panelin hangisi olacağını döner.
       *
       * Panel, girişin yapıldığı sekmeye göre belirlenir: Kursiyer sekmesinden
       * giren yönetici de kursiyer panelinde açılır. Yalnızca user type'a
       * bakıldığında yönetici hesabı hangi sekmeden girerse girsin yönetici
       * paneline düşüyordu. Yönetici sekmesi, hesabın yönetici girişine
       * yetkili olduğunu ayrıca doğrular. Giriş sekmesi hatırlandığı için
       * (bkz. loginRole.utils) yenilemede yanlış forma düşme sorunu geri gelmez.
       *
       * `panel` verilmezse eski davranış: user type'a göre.
       */
      setUserFromToken: (payload, panel) => {
        // euas-backend response: { success, data: { accessToken, refreshToken, user, expiresIn } }
        const data = payload?.data as Record<string, unknown> | undefined;
        const auth = payload?.auth as Record<string, unknown> | undefined;
        const dataAuth = data?.auth as Record<string, unknown> | undefined;
        let user = data?.user ?? dataAuth?.user ?? auth?.user ?? payload?.user;

        if (!user && payload?.sub) {
          user = {
            id: String(payload.sub ?? ""),
            email: String((payload as Record<string, unknown>).email ?? ""),
            phoneNumber: (payload as Record<string, unknown>).phone as
              | string
              | undefined,
            role: (payload as Record<string, unknown>).role as
              | string
              | undefined,
            is_user: (payload as Record<string, unknown>).userType === "user",
            firstName: String(
              (payload as Record<string, unknown>).firstName ??
                (payload as Record<string, unknown>).given_name ??
                "",
            ),
            lastName: String(
              (payload as Record<string, unknown>).lastName ??
                (payload as Record<string, unknown>).family_name ??
                "",
            ),
          };
        }

        const userType = getUserTypeFromAuthResponse(payload);
        // Type okunamazsa güvenli taraf: kursiyer paneli.
        const canUseAdminPanel =
          userType !== null && isAdminLoginAllowed(userType);
        const isStaff = panel === "client" ? false : canUseAdminPanel;

        set({
          currentUser: (user as CurrentUser | null | undefined) ?? null,
          isInitialized: true,
          isStaff,
          userType: userType ?? null,
          logo: null,
        });

        // Kullanıcı login olur olmaz permission'ları çek
        const state = get();
        if (state.currentUser?.id) {
          state
            .fetchCurrentUser(true)
            .catch((error: unknown) =>
              console.error(
                "❌ Error fetching permissions after login:",
                error,
              ),
            );
        }

        return isStaff;
      },
    }),
    {
      name: "store",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export const fetchCurrentUser = (forceRefresh = false) =>
  useStore.getState().fetchCurrentUser(forceRefresh);
export const refreshCurrentUser = () =>
  useStore.getState().refreshCurrentUser();
export const clearCurrentUser = () => useStore.getState().clearCurrentUser();
export const setUserFromToken = (
  payload: Record<string, unknown>,
  panel?: LoginPanel,
) => useStore.getState().setUserFromToken(payload, panel);

export const hasViewPermission = (permission: string, module?: string) => {
  const { permissionsArray } = useStore.getState();

  if (module) {
    const modulePermissions = permissionsArray.filter((perm) =>
      perm.startsWith(module),
    );
    return modulePermissions.length > 0;
  }

  return permissionsArray.includes(permission);
};

export const hasAllPermissions = (permissions: string[]) => {
  const { permissionsArray } = useStore.getState();
  return permissions.every((permission) =>
    permissionsArray.includes(permission),
  );
};

export const hasAnyPermission = (permissions: string[]) => {
  const { permissionsArray } = useStore.getState();
  return permissions.some((permission) =>
    permissionsArray.includes(permission),
  );
};
